import dayjs, { Dayjs } from "dayjs";

import type { LeaseContract } from "@/api/generated";
import type { PropertyHolder } from "@/view_models/atoms";

// 収入ソースの判定を賃料表示と利回り計算で共用する。
export const usesRoomIncome = (property: PropertyHolder): boolean =>
  property.property.property_category === "一棟物件" || property.lease_contracts.length === 0;

// 開始済み契約のうち開始日が最も新しい契約を採用する。空室中も直近の登録賃料を参照する。
// 開始済み契約がない物件では最初の予定契約を採用し、それ以降の賃料改定は先取りしない。
export const getLeaseContractForDate = (
  leaseContracts: LeaseContract[],
  asOf: Dayjs = dayjs()
): LeaseContract | undefined => {
  if (!asOf.isValid()) return undefined;
  const contracts = leaseContracts
    .filter((contract) => !!contract.starts_at && dayjs(contract.starts_at).isValid())
    .sort((a, b) => dayjs(a.starts_at).valueOf() - dayjs(b.starts_at).valueOf());
  const started = contracts.filter((contract) => !dayjs(contract.starts_at).isAfter(asOf, "day"));
  return started[started.length - 1] ?? contracts[0];
};

// 賃料表示は家賃＋管理費。利回りの収入には、別途その他月額収入も含む。
export const getMonthlyFeeFromProperty = (leaseContracts: LeaseContract[], asOf: Dayjs = dayjs()): number => {
  const contract = getLeaseContractForDate(leaseContracts, asOf);
  return (contract?.monthly_fee ?? 0) + (contract?.monthly_management_fee ?? 0);
};

// 一棟と、契約がなく部屋情報だけを持つ区分の賃料表示にも対応する。
// 部屋情報の賃料にはその他収入を含む（既存の一棟表示に合わせる）。
export const getMonthlyRentByProperty = (property: PropertyHolder, asOf: Dayjs = dayjs()): number => {
  if (usesRoomIncome(property)) {
    return property.building_room?.reduce(
      (sum, room) => sum + (room.monthly_fee ?? 0) + (room.other_monthly_income ?? 0),
      0
    ) ?? 0;
  }
  return getMonthlyFeeFromProperty(property.lease_contracts, asOf);
};

// 現在入居中の物件があるかどうか判定する関数
export const isPropertyCurrentInUse = (leaseContracts: LeaseContract[]): boolean => {
  const now = dayjs(new Date());
  return leaseContracts.some((contract) => {
    const start = dayjs(contract.starts_at);
    const end = dayjs(contract.ends_at);
    return now >= start && now <= end;
  });
};
